import { proxy } from 'valtio'
import { setAiGeneratedImageWatermarkEnabled } from '@/shared/lib/oss-url-utils'

export type AiWatermarkDialogTrigger = 'manual' | 'first_image_submit'

interface SyncAiWatermarkPreferenceInput {
  userKey?: string | null
  teamId?: string | null
  aiWatermark?: number | null
}

interface AiWatermarkPreferenceState {
  open: boolean
  trigger: AiWatermarkDialogTrigger | null
  removeAiTag: boolean
  saving: boolean
  confirmed: boolean
  userKey: string
  teamId: string
  serverAiWatermark: number | null
}

const CONFIRMED_STORAGE_KEY_PREFIX = 'ai-canvas:ai-watermark-confirmed:'
const DEFAULT_TEAM_STORAGE_KEY = '__default__'

function normalizeString(value: string | null | undefined) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeAiWatermark(value: number | null | undefined) {
  if (value === 0 || value === 1) {
    return value
  }

  return null
}

function buildConfirmedStorageKey(userKey: string, teamId: string) {
  return `${CONFIRMED_STORAGE_KEY_PREFIX}${userKey}:${teamId || DEFAULT_TEAM_STORAGE_KEY}`
}

function readConfirmed(userKey: string, teamId: string) {
  if (typeof window === 'undefined' || !userKey) {
    return false
  }

  try {
    return window.localStorage.getItem(buildConfirmedStorageKey(userKey, teamId)) === '1'
  } catch {
    return false
  }
}

function writeConfirmed(userKey: string, teamId: string, confirmed: boolean) {
  if (typeof window === 'undefined' || !userKey) {
    return
  }

  try {
    const key = buildConfirmedStorageKey(userKey, teamId)
    if (confirmed) {
      window.localStorage.setItem(key, '1')
      return
    }

    window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function resolveRemoveAiTag(aiWatermark: number | null) {
  return aiWatermark === 0
}

function resetAiWatermarkPreferenceState() {
  aiWatermarkPreferenceStore.open = false
  aiWatermarkPreferenceStore.trigger = null
  aiWatermarkPreferenceStore.removeAiTag = false
  aiWatermarkPreferenceStore.saving = false
  aiWatermarkPreferenceStore.confirmed = false
  aiWatermarkPreferenceStore.userKey = ''
  aiWatermarkPreferenceStore.teamId = ''
  aiWatermarkPreferenceStore.serverAiWatermark = null
  setAiGeneratedImageWatermarkEnabled(true)
}

export const aiWatermarkPreferenceStore = proxy<AiWatermarkPreferenceState>({
  open: false,
  trigger: null,
  removeAiTag: false,
  saving: false,
  confirmed: false,
  userKey: '',
  teamId: '',
  serverAiWatermark: null,
})

let pendingGuardPromise: Promise<boolean> | null = null
let pendingGuardResolve: ((result: boolean) => void) | null = null

export function resolveAiWatermarkGuard(result: boolean) {
  pendingGuardResolve?.(result)
  pendingGuardResolve = null
  pendingGuardPromise = null
}

export function clearAiWatermarkPreference() {
  resolveAiWatermarkGuard(false)
  resetAiWatermarkPreferenceState()
}

export function syncAiWatermarkPreference(input: SyncAiWatermarkPreferenceInput) {
  const userKey = normalizeString(input.userKey)
  const teamId = normalizeString(input.teamId)

  if (!userKey) {
    clearAiWatermarkPreference()
    return
  }

  const previousUserKey = aiWatermarkPreferenceStore.userKey
  const previousTeamId = aiWatermarkPreferenceStore.teamId
  const previousServerAiWatermark = aiWatermarkPreferenceStore.serverAiWatermark
  const sameContext = previousUserKey === userKey && previousTeamId === teamId
  const normalizedAiWatermark = normalizeAiWatermark(input.aiWatermark)

  if (!sameContext) {
    resolveAiWatermarkGuard(false)
    aiWatermarkPreferenceStore.open = false
    aiWatermarkPreferenceStore.trigger = null
    aiWatermarkPreferenceStore.saving = false
  }

  const nextServerAiWatermark =
    normalizedAiWatermark ?? (previousUserKey === userKey ? previousServerAiWatermark : null)

  aiWatermarkPreferenceStore.userKey = userKey
  aiWatermarkPreferenceStore.teamId = teamId
  aiWatermarkPreferenceStore.serverAiWatermark = nextServerAiWatermark
  aiWatermarkPreferenceStore.confirmed = readConfirmed(userKey, teamId)
  setAiGeneratedImageWatermarkEnabled(nextServerAiWatermark !== 0)

  if (!aiWatermarkPreferenceStore.open || !sameContext) {
    aiWatermarkPreferenceStore.removeAiTag = resolveRemoveAiTag(nextServerAiWatermark)
  }
}

export function openAiWatermarkDialog(trigger: AiWatermarkDialogTrigger) {
  aiWatermarkPreferenceStore.open = true
  aiWatermarkPreferenceStore.trigger = trigger
  aiWatermarkPreferenceStore.saving = false
  aiWatermarkPreferenceStore.removeAiTag = resolveRemoveAiTag(aiWatermarkPreferenceStore.serverAiWatermark)
}

export function closeAiWatermarkDialog() {
  if (aiWatermarkPreferenceStore.saving) {
    return
  }

  const shouldRejectGuard = aiWatermarkPreferenceStore.trigger === 'first_image_submit'

  aiWatermarkPreferenceStore.open = false
  aiWatermarkPreferenceStore.trigger = null

  if (shouldRejectGuard) {
    resolveAiWatermarkGuard(false)
  }
}

export function setAiWatermarkRemoveEnabled(enabled: boolean) {
  aiWatermarkPreferenceStore.removeAiTag = enabled
}

export function markAiWatermarkSaving(saving: boolean) {
  aiWatermarkPreferenceStore.saving = saving
}

export function confirmAiWatermarkPreference(aiWatermark: number) {
  const normalizedAiWatermark = normalizeAiWatermark(aiWatermark)

  if (normalizedAiWatermark === null) {
    return
  }

  aiWatermarkPreferenceStore.serverAiWatermark = normalizedAiWatermark
  aiWatermarkPreferenceStore.removeAiTag = resolveRemoveAiTag(normalizedAiWatermark)
  aiWatermarkPreferenceStore.confirmed = true
  aiWatermarkPreferenceStore.saving = false
  setAiGeneratedImageWatermarkEnabled(normalizedAiWatermark !== 0)

  if (aiWatermarkPreferenceStore.userKey) {
    writeConfirmed(
      aiWatermarkPreferenceStore.userKey,
      aiWatermarkPreferenceStore.teamId,
      true,
    )
  }

  const shouldResolveGuard = aiWatermarkPreferenceStore.trigger === 'first_image_submit'

  aiWatermarkPreferenceStore.open = false
  aiWatermarkPreferenceStore.trigger = null

  if (shouldResolveGuard) {
    resolveAiWatermarkGuard(true)
  }
}

export function ensureAiWatermarkReadyForImageSubmit() {
  if (!aiWatermarkPreferenceStore.userKey || aiWatermarkPreferenceStore.confirmed) {
    return Promise.resolve(true)
  }

  openAiWatermarkDialog('first_image_submit')

  if (!pendingGuardPromise) {
    pendingGuardPromise = new Promise<boolean>((resolve) => {
      pendingGuardResolve = resolve
    })
  }

  return pendingGuardPromise
}
